import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react'
import type { TranscriptPartialSource } from '@shared/transcript-feed'
import type { LivePartial } from './use-partial-transcriptions'
import { joinWithoutRepeats } from './text-overlap'

/** Gives up on a final that never lands (an empty or filtered result) so the text can't linger as a ghost. */
const HOLD_TIMEOUT_MS = 3_000

const SOURCES: ReadonlyArray<TranscriptPartialSource> = ['loopback', 'microphone']

/** The in-flight line on screen. */
export interface ShownPartial {
  source: TranscriptPartialSource
  text: string
  /** Wall-clock ms the utterance was first shown. Stable for its whole life, unlike the provider's timestamps. */
  startedAt: number
}

/**
 * A source's live utterance, told apart from the next by the provider's `startTimestamp`.
 * `finalLanded` once an entry from its source arrived while it was still live.
 */
type LiveUtterance = ShownPartial & { startTimestamp: number; finalLanded: boolean }

export interface HeldPartialState {
  /** Transcript entries already accounted for. */
  seenLength: number
  /** Each source's live utterance, tracked apart so overlapping speech doesn't end the other speaker's. */
  live: Partial<Record<TranscriptPartialSource, LiveUtterance>>
  /** Each source's last finalized utterance, while its final hasn't reached the transcript. */
  held: Partial<Record<TranscriptPartialSource, ShownPartial>>
}

/** Only what the hold needs from a transcript entry: which capture it came from. */
export interface HeldPartialEntry {
  isHost: boolean
}

const sourceOfEntry = (entry: HeldPartialEntry): TranscriptPartialSource => (entry.isHost ? 'microphone' : 'loopback')

/**
 * The partial is cleared the moment the provider finalizes it, but the labeled
 * transcript only receives that final after processing, ~500ms later. Holding
 * the text across the gap keeps the turn from shrinking and regrowing.
 *
 * A source holds one line at most, released by the next entry from that
 * source: processed finals don't map one-to-one onto provider utterances, so
 * counting them off against held lines lets text pile up that the transcript
 * already shows. New live text from the source is joined after the held line
 * without the words it re-sends from it, which providers often do.
 */
export function nextHeldPartialState(
  state: HeldPartialState,
  lives: ReadonlyArray<LivePartial>,
  entries: ReadonlyArray<HeldPartialEntry>,
  now: number,
): HeldPartialState {
  // A shrunk buffer was replaced (a reset or a reprocess): the held lines' finals went with the old one
  const isReset = entries.length < state.seenLength
  let held = isReset ? {} : state.held
  let live = state.live

  const newEntries = isReset ? [] : entries.slice(state.seenLength)
  for (const source of new Set(newEntries.map(sourceOfEntry))) {
    const utterance = live[source]
    if (held[source]) {
      held = { ...held, [source]: undefined }
    } else if (utterance && !utterance.finalLanded) {
      // Its final beat the partial clearing; holding it would show the text twice
      live = { ...live, [source]: { ...utterance, finalLanded: true } }
    }
  }

  for (const source of SOURCES) {
    const previous = live[source]
    const current = lives.find((partial) => partial.source === source && partial.text)
    if (previous && current?.startTimestamp === previous.startTimestamp) {
      if (previous.text !== current.text) live = { ...live, [source]: { ...previous, text: current.text } }
      continue
    }
    if (previous && !previous.finalLanded && !isReset) {
      const { text, startedAt } = previous
      held = { ...held, [source]: { source, text, startedAt } }
    }
    const nextUtterance: LiveUtterance | undefined = current && {
      source,
      text: current.text,
      startTimestamp: current.startTimestamp,
      startedAt: now,
      finalLanded: false,
    }
    if (nextUtterance || previous) live = { ...live, [source]: nextUtterance }
  }

  if (live === state.live && held === state.held && entries.length === state.seenLength) return state
  return { seenLength: entries.length, live, held }
}

/** The newest source's held line joined ahead of its live text, as one line. */
export function selectShownPartial(
  state: HeldPartialState,
  lives: ReadonlyArray<LivePartial>,
): ShownPartial | undefined {
  const source =
    lives[lives.length - 1]?.source ??
    SOURCES.flatMap((s) => state.held[s] ?? [])
      .sort((a, b) => a.startedAt - b.startedAt)
      .at(-1)?.source
  if (!source) return undefined
  const held = state.held[source]
  const live = state.live[source]
  if (held && live) return { ...held, text: joinWithoutRepeats(held.text, live.text) }
  const line = live ?? held
  return line && { source, text: line.text, startedAt: line.startedAt }
}

/**
 * The newest live partial, plus any just-finalized text from its source the
 * transcript hasn't caught up to. `lives` is every source's partial, oldest first.
 */
export function useHeldPartial(
  lives: ReadonlyArray<LivePartial>,
  entries: ReadonlyArray<HeldPartialEntry>,
): ShownPartial | undefined {
  // Starts level with the transcript, so a backlog already on screen isn't taken for new finals
  const [state, setState] = useState<HeldPartialState>(() => ({ seenLength: entries.length, live: {}, held: {} }))
  const next = nextHeldPartialState(state, lives, entries, Date.now())
  // Adjusted during render, not in an effect, so the frame that clears the partial already shows the hold
  if (next !== state) setState(next)

  useHoldTimeout(next.held.loopback, setState)
  useHoldTimeout(next.held.microphone, setState)

  const shown = selectShownPartial(next, lives)
  // One object per line state, not per render, so consumers can memo on it
  const { source, text, startedAt } = shown ?? {}
  return useMemo(
    () => (source && text !== undefined && startedAt !== undefined ? { source, text, startedAt } : undefined),
    [source, text, startedAt],
  )
}

function useHoldTimeout(line: ShownPartial | undefined, setState: Dispatch<SetStateAction<HeldPartialState>>) {
  useEffect(() => {
    if (!line) return
    const timer = setTimeout(
      () =>
        setState((current) =>
          current.held[line.source] === line
            ? { ...current, held: { ...current.held, [line.source]: undefined } }
            : current,
        ),
      HOLD_TIMEOUT_MS,
    )
    return () => clearTimeout(timer)
  }, [line, setState])
}
